import Phaser from 'phaser';
import { IsoGrid } from '@/iso/IsoGrid';
import { GRID_COLS, GRID_ROWS, EquipmentType } from '@/config';
import { EventBus, GameEvent } from '@/utils/EventBus';
import { lerpColor } from '@/utils/ColorUtils';

interface EquipmentInfo {
  type: EquipmentType;
  col: number;
  row: number;
}

enum SpecialState {
  ENTERING,
  ACTIVE,
  LEAVING,
}

interface SpecialCharacter {
  name: string;
  sprite: Phaser.GameObjects.Image;
  label: Phaser.GameObjects.Text;
  speechBubble: Phaser.GameObjects.Text | null;
  col: number;
  row: number;
  targetCol: number;
  targetRow: number;
  state: SpecialState;
  tickTimer: number;
  extraData: Record<string, any>;
}

/**
 * Manages the 6 special named characters with unique behaviors.
 */
export class SpecialCharacterManager {
  private scene: Phaser.Scene;
  private grid: IsoGrid;
  private characters: Map<string, SpecialCharacter> = new Map();
  private equipment: EquipmentInfo[] = [];
  private spawned = false;
  private spawnDelay = 0;

  // Staggered spawn queue
  private pendingSpawns: Array<{ name: string; texture: string; col: number; row: number; delay: number }> = [];
  private spawnElapsed = 0;

  // Pedro's RPG projectiles
  private projectiles: Array<{ sprite: Phaser.GameObjects.Image; startY: number; vy: number; life: number }> = [];

  constructor(scene: Phaser.Scene, grid: IsoGrid) {
    this.scene = scene;
    this.grid = grid;

    EventBus.on(GameEvent.EQUIPMENT_PLACED, () => this.refreshEquipment());
    EventBus.on(GameEvent.EQUIPMENT_REMOVED, () => this.refreshEquipment());
  }

  /** Get all spawned characters for state snapshots */
  getCharacters(): Array<{ name: string; col: number; row: number; speechText: string | null }> {
    const result: Array<{ name: string; col: number; row: number; speechText: string | null }> = [];
    for (const char of this.characters.values()) {
      result.push({
        name: char.name,
        col: char.col,
        row: char.row,
        speechText: char.speechBubble?.text ?? null,
      });
    }
    return result;
  }

  private refreshEquipment(): void {
    // Will be called with latest equipment on next tick
  }

  /** Call with current equipment list each tick */
  update(deltaMs: number, equipment: EquipmentInfo[]): void {
    this.equipment = equipment;

    // Don't spawn until there's a DJ booth
    const hasDJ = equipment.some((e) => e.type === EquipmentType.DJ_BOOTH);
    if (!hasDJ) return;

    if (!this.spawned) {
      this.spawnDelay += deltaMs;
      // Queue special characters 3 seconds after DJ booth placed
      if (this.spawnDelay > 3000) {
        this.spawnAllCharacters();
        this.spawned = true;
      }
      return;
    }

    // Drain pending spawn queue
    if (this.pendingSpawns.length > 0) {
      this.spawnElapsed += deltaMs;
      while (this.pendingSpawns.length > 0 && this.pendingSpawns[0].delay <= this.spawnElapsed) {
        const s = this.pendingSpawns.shift()!;
        this.spawnCharacter(s.name, s.texture, s.col, s.row);
      }
    }

    // Update each character
    this.updateJames(deltaMs);
    this.updateKevin(deltaMs);
    this.updateAndrew(deltaMs);
    this.updateStu(deltaMs);
    this.updatePedro(deltaMs);
    this.updateMike(deltaMs);
    this.updateClaude(deltaMs);
    this.updatePoggy(deltaMs);
    this.updateEarl(deltaMs);

    // Update projectiles
    this.updateProjectiles(deltaMs);

    // Move characters toward targets and update sprite positions
    for (const char of this.characters.values()) {
      // Earl moves every other tick (half speed — leisurely bike ride)
      if (char.name === 'earl') {
        char.extraData.moveSkip = !char.extraData.moveSkip;
        if (!char.extraData.moveSkip) {
          this.moveTowardTarget(char);
        }
      } else {
        this.moveTowardTarget(char);
      }
      this.updateSpritePosition(char);
    }
  }

  private spawnAllCharacters(): void {
    const entries: Array<[string, string, number, number]> = [
      ['james', 'special-james', 0, Math.floor(GRID_ROWS / 2)],
      ['kevin', 'special-kevin', Math.floor(GRID_COLS / 2), 0],
      ['andrew', 'special-andrew', GRID_COLS - 1, Math.floor(GRID_ROWS / 2)],
      ['stu', 'special-stu', Math.floor(GRID_COLS / 2), GRID_ROWS - 1],
      ['pedro', 'special-pedro', 0, GRID_ROWS - 1],
      ['mike', 'special-mike', GRID_COLS - 1, 0],
      ['claude', 'special-claude', Math.floor(GRID_COLS / 2), GRID_ROWS - 1],
      ['poggy', 'special-poggy', 0, 0],
      ['earl', 'special-earl', 0, 0],
    ];

    // Shuffle order
    for (let i = entries.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [entries[i], entries[j]] = [entries[j], entries[i]];
    }

    // Stagger spawns: each character gets a random delay within a ~5s window
    for (let i = 0; i < entries.length; i++) {
      const [name, texture, col, row] = entries[i];
      const delay = i * 800 + Math.random() * 400;
      this.pendingSpawns.push({ name, texture, col, row, delay });
    }
  }

  private spawnCharacter(name: string, textureKey: string, col: number, row: number): void {
    const world = this.grid.gridToWorld(col, row);
    const sprite = this.scene.add.image(world.x, world.y, textureKey);
    sprite.setDepth(world.y + 6);

    // Name label above character
    const label = this.scene.add.text(world.x, world.y - 18, name.toUpperCase(), {
      fontFamily: 'monospace',
      fontSize: '8px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 2,
      align: 'center',
    });
    label.setOrigin(0.5, 1);
    label.setDepth(1001);

    const char: SpecialCharacter = {
      name,
      sprite,
      label,
      speechBubble: null,
      col,
      row,
      targetCol: col,
      targetRow: row,
      state: SpecialState.ENTERING,
      tickTimer: 0,
      extraData: {},
    };

    this.characters.set(name, char);
  }

  private moveTowardTarget(char: SpecialCharacter): void {
    if (char.col === char.targetCol && char.row === char.targetRow) return;

    // Move one step
    if (char.col < char.targetCol) char.col++;
    else if (char.col > char.targetCol) char.col--;

    if (char.row < char.targetRow) char.row++;
    else if (char.row > char.targetRow) char.row--;
  }

  private updateSpritePosition(char: SpecialCharacter): void {
    const world = this.grid.gridToWorld(char.col, char.row);
    char.sprite.setPosition(world.x, world.y);
    char.sprite.setDepth(world.y + 6);
    char.label.setPosition(world.x, world.y - 18);
    char.label.setDepth(1001);

    if (char.speechBubble) {
      char.speechBubble.setPosition(world.x, world.y - 28);
      char.speechBubble.setDepth(1002);
    }
  }

  private showSpeechBubble(char: SpecialCharacter, text: string, duration: number = 3000): void {
    if (char.speechBubble) {
      char.speechBubble.destroy();
    }

    const world = this.grid.gridToWorld(char.col, char.row);
    char.speechBubble = this.scene.add.text(world.x, world.y - 28, text, {
      fontFamily: 'monospace',
      fontSize: '7px',
      color: '#ffff00',
      stroke: '#000000',
      strokeThickness: 3,
      align: 'center',
      backgroundColor: '#000000aa',
      padding: { x: 3, y: 2 },
    });
    char.speechBubble.setOrigin(0.5, 1);
    char.speechBubble.setDepth(1002);

    this.scene.time.delayedCall(duration, () => {
      if (char.speechBubble) {
        char.speechBubble.destroy();
        char.speechBubble = null;
      }
    });
  }

  // ─── DJ booth flees from James (Beauty and the Beast style) ───

  private fleeDjBoothFromJames(char: SpecialCharacter): void {
    const dj = this.equipment.find((e) => e.type === EquipmentType.DJ_BOOTH);
    if (!dj) return;

    // Only flee if James is within 3 tiles
    const dist = Math.abs(char.col - dj.col) + Math.abs(char.row - dj.row);
    if (dist > 3) return;

    // Cooldown: don't flee more than once every 2 seconds
    const now = Date.now();
    const lastFlee = char.extraData.djFleeTime || 0;
    if (now - lastFlee < 2000) return;

    // Direction away from James
    const dx = dj.col - char.col;
    const dy = dj.row - char.row;

    // Flee 3-4 tiles away from James
    const fleeDistance = 3 + Math.floor(Math.random() * 2);
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    let newCol = Math.round(dj.col + (dx / len) * fleeDistance);
    let newRow = Math.round(dj.row + (dy / len) * fleeDistance);

    // Clamp to grid bounds
    newCol = Math.max(0, Math.min(GRID_COLS - 1, newCol));
    newRow = Math.max(0, Math.min(GRID_ROWS - 1, newRow));

    // Don't move to where we already are
    if (newCol === dj.col && newRow === dj.row) return;

    // Check the destination isn't occupied (scan nearby if it is)
    if (this.grid.isCellOccupied(newCol, newRow)) {
      // Try a few offsets
      const offsets = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [-1, -1]];
      let found = false;
      for (const [ox, oy] of offsets) {
        const tryCol = newCol + ox;
        const tryRow = newRow + oy;
        if (tryCol >= 0 && tryCol < GRID_COLS && tryRow >= 0 && tryRow < GRID_ROWS && !this.grid.isCellOccupied(tryCol, tryRow)) {
          newCol = tryCol;
          newRow = tryRow;
          found = true;
          break;
        }
      }
      if (!found) return;
    }

    char.extraData.djFleeTime = now;

    EventBus.emit(GameEvent.EQUIPMENT_MOVED, {
      type: EquipmentType.DJ_BOOTH,
      fromCol: dj.col,
      fromRow: dj.row,
      toCol: newCol,
      toRow: newRow,
    });

    // Update our local equipment reference immediately
    dj.col = newCol;
    dj.row = newRow;
  }

  // ─── JAMES: Goes to bar for "Coke", then pushes behind DJ booth ───

  private updateJames(deltaMs: number): void {
    const char = this.characters.get('james');
    if (!char) return;
    char.tickTimer += deltaMs;

    // DJ booth flees from James when he gets close
    if (char.state === SpecialState.ACTIVE) {
      this.fleeDjBoothFromJames(char);
    }

    if (char.state === SpecialState.ENTERING) {
      // Head to bar first
      const bar = this.equipment.find((e) => e.type === EquipmentType.BAR);
      if (bar) {
        char.targetCol = bar.col;
        char.targetRow = bar.row;
      } else {
        char.targetCol = Math.floor(GRID_COLS / 2);
        char.targetRow = Math.floor(GRID_ROWS / 2);
      }
      char.state = SpecialState.ACTIVE;
      char.extraData.phase = 'going_to_bar';
      char.tickTimer = 0;
    }

    if (char.state === SpecialState.ACTIVE) {
      const atTarget = char.col === char.targetCol && char.row === char.targetRow;

      if (char.extraData.phase === 'going_to_bar' && atTarget) {
        this.showSpeechBubble(char, 'Can I get some Coke?', 4000);
        char.extraData.phase = 'at_bar';
        char.tickTimer = 0;
      }

      if (char.extraData.phase === 'at_bar' && char.tickTimer > 5000) {
        // Now push to behind the DJ booth
        const dj = this.equipment.find((e) => e.type === EquipmentType.DJ_BOOTH);
        if (dj) {
          // "Behind" the DJ booth = one row above it (toward top of iso view)
          char.targetCol = dj.col;
          char.targetRow = Math.max(0, dj.row - 1);
          this.showSpeechBubble(char, 'Excuse me, coming through!', 3000);
        }
        char.extraData.phase = 'pushing_to_dj';
        char.tickTimer = 0;
      }

      // Keep retargeting the DJ booth as it flees
      if (char.extraData.phase === 'pushing_to_dj' && !atTarget) {
        const dj = this.equipment.find((e) => e.type === EquipmentType.DJ_BOOTH);
        if (dj) {
          char.targetCol = dj.col;
          char.targetRow = Math.max(0, dj.row - 1);
        }
      }

      if (char.extraData.phase === 'pushing_to_dj' && atTarget && char.tickTimer > 4000) {
        const songRequests = [
          'Play Sandstorm!',
          'Play Levels!',
          'Play Blue!',
          'Play Insomnia!',
          'Play Children!',
          'Play Kernkraft 400!',
          'Play Born Slippy!',
          'Play Firestarter!',
          'Play Better Off Alone!',
          'Play No Limit!',
          'Play Rhythm Is A Dancer!',
        ];
        const song = songRequests[Math.floor(Math.random() * songRequests.length)];
        this.showSpeechBubble(char, `Hey DJ! ${song}`, 4000);
        char.extraData.phase = 'behind_dj';
        char.tickTimer = 0;
      }

      // Cycle between bar and DJ booth
      if (char.extraData.phase === 'behind_dj' && char.tickTimer > 12000) {
        const bar = this.equipment.find((e) => e.type === EquipmentType.BAR);
        if (bar) {
          char.targetCol = bar.col;
          char.targetRow = bar.row;
        }
        char.extraData.phase = 'going_to_bar';
        char.tickTimer = 0;
        this.showSpeechBubble(char, 'Need another Coke!', 3000);
      }
    }
  }

  // ─── KEVIN: Asks about craft beers ───

  private updateKevin(deltaMs: number): void {
    const char = this.characters.get('kevin');
    if (!char) return;
    char.tickTimer += deltaMs;

    if (char.state === SpecialState.ENTERING) {
      const bar = this.equipment.find((e) => e.type === EquipmentType.BAR);
      if (bar) {
        char.targetCol = Math.min(bar.col + 1, GRID_COLS - 1);
        char.targetRow = bar.row;
      } else {
        char.targetCol = Math.floor(GRID_COLS / 2) + 2;
        char.targetRow = Math.floor(GRID_ROWS / 2);
      }
      char.state = SpecialState.ACTIVE;
      char.extraData.beerIndex = 0;
      char.tickTimer = 0;
    }

    if (char.state === SpecialState.ACTIVE) {
      const atTarget = char.col === char.targetCol && char.row === char.targetRow;

      if (atTarget && char.tickTimer > 6000) {
        const beerQuestions = [
          "Any IPAs on tap?",
          "Is that a hazy or west coast?",
          "Do you have anything barrel-aged?",
          "What's the IBU on that?",
          "Any local sours?",
          "Is that a double or triple IPA?",
          "Got any NEIPAs?",
          "What's your freshest hop?",
        ];
        const idx = char.extraData.beerIndex % beerQuestions.length;
        this.showSpeechBubble(char, beerQuestions[idx], 5000);
        char.extraData.beerIndex++;
        char.tickTimer = 0;
      }
    }
  }

  // ─── ANDREW: Dancing maniac ───

  private updateAndrew(deltaMs: number): void {
    const char = this.characters.get('andrew');
    if (!char) return;
    char.tickTimer += deltaMs;

    if (char.state === SpecialState.ENTERING) {
      // Head to center of the dance floor
      char.targetCol = Math.floor(GRID_COLS / 2);
      char.targetRow = Math.floor(GRID_ROWS / 2);
      char.state = SpecialState.ACTIVE;
      char.extraData.shirtOff = false;
      char.extraData.shirtTimer = 0;
      char.tickTimer = 0;
    }

    if (char.state === SpecialState.ACTIVE) {
      char.extraData.shirtTimer = (char.extraData.shirtTimer || 0) + deltaMs;

      // Take shirt off after 10 seconds of dancing
      if (!char.extraData.shirtOff && char.extraData.shirtTimer > 10000) {
        char.sprite.setTexture('special-andrew-shirtless');
        char.extraData.shirtOff = true;
        this.showSpeechBubble(char, "SHIRTS OFF LET'S GO!", 4000);
      }

      // Wild dancing animation - rapid position oscillation
      const world = this.grid.gridToWorld(char.col, char.row);
      const t = Date.now() * 0.008;
      const wobbleX = Math.sin(t * 3) * 6;
      const wobbleY = Math.cos(t * 4) * 4 + Math.abs(Math.sin(t * 6)) * -8;
      const rotation = Math.sin(t * 2) * 0.3;

      char.sprite.setPosition(world.x + wobbleX, world.y + wobbleY);
      char.sprite.setRotation(rotation);

      // Occasionally shout
      if (char.tickTimer > 8000) {
        const shouts = [
          'WOOOO!',
          'DROP THE BASS!',
          'LETS GOOO!',
          '*breakdancing*',
          'THIS IS MY JAM!',
          '*windmill arms*',
          '*swinging shirt overhead*',
        ];
        const idx = Math.floor(Math.random() * shouts.length);
        this.showSpeechBubble(char, shouts[idx], 3000);
        char.tickTimer = 0;
      }

      // Update label position manually since we override sprite position
      char.label.setPosition(world.x + wobbleX, world.y + wobbleY - 18);
      if (char.speechBubble) {
        char.speechBubble.setPosition(world.x + wobbleX, world.y + wobbleY - 28);
      }

      // Skip the normal position update for Andrew
      return;
    }
  }

  // ─── STU: Bald head changes colors white → red ───

  private updateStu(deltaMs: number): void {
    const char = this.characters.get('stu');
    if (!char) return;
    char.tickTimer += deltaMs;

    if (char.state === SpecialState.ENTERING) {
      char.targetCol = Math.floor(GRID_COLS / 2) - 2;
      char.targetRow = Math.floor(GRID_ROWS / 2) + 2;
      char.state = SpecialState.ACTIVE;
      char.extraData.phase = 'chilling';
      char.extraData.panicTimer = 0;
      char.tickTimer = 0;
    }

    if (char.state === SpecialState.ACTIVE) {
      // Head color cycles from white (0xffffff) to red (0xff0000) and back
      const t = (Math.sin(Date.now() * 0.002) + 1) / 2; // 0-1 oscillation
      const color = lerpColor(0xffffff, 0xff0000, t);
      char.sprite.setTint(color);

      if (char.extraData.phase === 'chilling') {
        char.extraData.panicTimer = (char.extraData.panicTimer || 0) + deltaMs;

        if (char.tickTimer > 10000) {
          // Wander to a random nearby spot
          char.targetCol = Math.floor(GRID_COLS / 2) + Math.floor(Math.random() * 6) - 3;
          char.targetRow = Math.floor(GRID_ROWS / 2) + Math.floor(Math.random() * 6) - 3;
          char.targetCol = Math.max(0, Math.min(GRID_COLS - 1, char.targetCol));
          char.targetRow = Math.max(0, Math.min(GRID_ROWS - 1, char.targetRow));
          char.tickTimer = 0;
        }

        // Periodically panic about students
        if (char.extraData.panicTimer > 15000) {
          this.showSpeechBubble(char, 'Oh god, is that a student?!', 3000);
          char.extraData.phase = 'panicking';
          char.extraData.panicTimer = 0;
          char.tickTimer = 0;
        }
      }

      if (char.extraData.phase === 'panicking' && char.tickTimer > 2000) {
        // Run to a random edge
        this.showSpeechBubble(char, 'GOTTA GO!', 2000);
        const edge = Math.floor(Math.random() * 4);
        switch (edge) {
          case 0: char.targetCol = 0; char.targetRow = char.row; break;
          case 1: char.targetCol = GRID_COLS - 1; char.targetRow = char.row; break;
          case 2: char.targetCol = char.col; char.targetRow = 0; break;
          default: char.targetCol = char.col; char.targetRow = GRID_ROWS - 1; break;
        }
        char.extraData.phase = 'running_away';
        char.tickTimer = 0;
      }

      if (char.extraData.phase === 'running_away') {
        // Double speed: move an extra step toward target
        this.moveTowardTarget(char);

        const atEdge = char.col === char.targetCol && char.row === char.targetRow;
        if (atEdge || char.tickTimer > 8000) {
          // Hide briefly
          char.sprite.setAlpha(0);
          char.label.setAlpha(0);
          char.extraData.phase = 'hiding';
          char.tickTimer = 0;
        }
      }

      if (char.extraData.phase === 'hiding' && char.tickTimer > 6000) {
        // Sneak back
        const edge = Math.floor(Math.random() * 4);
        switch (edge) {
          case 0: char.col = 0; char.row = Math.floor(Math.random() * GRID_ROWS); break;
          case 1: char.col = GRID_COLS - 1; char.row = Math.floor(Math.random() * GRID_ROWS); break;
          case 2: char.col = Math.floor(Math.random() * GRID_COLS); char.row = 0; break;
          default: char.col = Math.floor(Math.random() * GRID_COLS); char.row = GRID_ROWS - 1; break;
        }
        char.targetCol = Math.floor(GRID_COLS / 2) + Math.floor(Math.random() * 4) - 2;
        char.targetRow = Math.floor(GRID_ROWS / 2) + Math.floor(Math.random() * 4) - 2;
        char.sprite.setAlpha(1);
        char.label.setAlpha(1);
        this.showSpeechBubble(char, 'Coast is clear...', 3000);
        char.extraData.phase = 'chilling';
        char.extraData.panicTimer = 0;
        char.tickTimer = 0;
      }
    }
  }

  // ─── PEDRO: Shoots RPG in the air + "Old choke and spit" dance ───

  private updatePedro(deltaMs: number): void {
    const char = this.characters.get('pedro');
    if (!char) return;
    char.tickTimer += deltaMs;

    if (char.state === SpecialState.ENTERING) {
      char.targetCol = Math.floor(GRID_COLS / 2) + 3;
      char.targetRow = Math.floor(GRID_ROWS / 2) - 2;
      char.state = SpecialState.ACTIVE;
      char.extraData.shotTimer = 0;
      char.tickTimer = 0;
    }

    if (char.state === SpecialState.ACTIVE) {
      char.extraData.shotTimer = (char.extraData.shotTimer || 0) + deltaMs;

      if (char.extraData.shotTimer > 10000) {
        // Fire RPG!
        this.showSpeechBubble(char, 'FIRE IN THE HOLE!', 2000);
        this.fireRpg(char);
        char.extraData.shotTimer = 0;
      }

      // Wander occasionally
      if (char.tickTimer > 15000) {
        char.targetCol = Math.floor(GRID_COLS / 2) + Math.floor(Math.random() * 4) - 2;
        char.targetRow = Math.floor(GRID_ROWS / 2) + Math.floor(Math.random() * 4) - 2;
        char.targetCol = Math.max(0, Math.min(GRID_COLS - 1, char.targetCol));
        char.targetRow = Math.max(0, Math.min(GRID_ROWS - 1, char.targetRow));
        char.tickTimer = 0;
      }

      // "Old choke and spit" dance animation
      this.animatePedroDance2D(char);
    }
  }

  /**
   * Pedro's "Old choke and spit" dance in 2D.
   * 4-phase 2-second cycle using sprite rotation, position offsets, and scale.
   *  Phase 1 (0–50%): Lean forward choking — body tilts, rapid bob
   *  Phase 2 (50–70%): Snap back upright with slight upward pop
   *  Phase 3 (70–90%): Spit — sharp head thrust forward
   *  Phase 4 (90–100%): Recover to neutral
   */
  private animatePedroDance2D(char: SpecialCharacter): void {
    const world = this.grid.gridToWorld(char.col, char.row);
    const t = Date.now() * 0.001; // time in seconds
    const cycle = (t * 0.5) % 1; // 2-second cycle, 0→1

    let rotation: number;
    let offsetX: number;
    let offsetY: number;
    let scaleX: number;

    if (cycle < 0.5) {
      // Phase 1: Lean & choke — forward tilt, rapid arm-pump bob
      const p = cycle / 0.5;
      rotation = p * 0.35; // lean forward
      offsetX = Math.sin(p * Math.PI * 4) * 2; // rapid side-to-side pump
      offsetY = Math.sin(p * Math.PI * 6) * 1.5; // rapid vertical bob
      scaleX = 1;
    } else if (cycle < 0.7) {
      // Phase 2: Snap back upright
      const p = (cycle - 0.5) / 0.2;
      rotation = 0.35 * (1 - p); // spring back
      offsetX = 0;
      offsetY = -p * 3; // slight upward pop
      scaleX = 1;
    } else if (cycle < 0.9) {
      // Phase 3: Spit — sharp forward thrust
      const p = (cycle - 0.7) / 0.2;
      const thrust = Math.sin(p * Math.PI); // peaks mid-phase
      rotation = thrust * 0.2;
      offsetX = thrust * 4; // thrust forward in iso X
      offsetY = (1 - p) * -3 + thrust * 1; // descend from pop + slight up on thrust
      scaleX = 1 + thrust * 0.15; // slight stretch on spit
    } else {
      // Phase 4: Recover to neutral
      const p = (cycle - 0.9) / 0.1;
      rotation = 0;
      offsetX = 0;
      offsetY = 0;
      scaleX = 1 + (1 - p) * 0.05; // ease back to normal
    }

    char.sprite.setPosition(world.x + offsetX, world.y + offsetY);
    char.sprite.setRotation(rotation);
    char.sprite.setScale(scaleX, 1);

    // Update label and speech bubble positions to follow
    char.label.setPosition(world.x + offsetX, world.y + offsetY - 18);
    if (char.speechBubble) {
      char.speechBubble.setPosition(world.x + offsetX, world.y + offsetY - 28);
    }
  }

  private fireRpg(char: SpecialCharacter): void {
    const world = this.grid.gridToWorld(char.col, char.row);
    const projectile = this.scene.add.image(world.x, world.y - 10, 'rpg-projectile');
    projectile.setDepth(2000);
    projectile.setBlendMode(Phaser.BlendModes.ADD);

    this.projectiles.push({
      sprite: projectile,
      startY: world.y - 10,
      vy: -3,
      life: 0,
    });
  }

  private updateProjectiles(deltaMs: number): void {
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const proj = this.projectiles[i];
      proj.life += deltaMs;
      proj.sprite.y += proj.vy;
      proj.vy -= 0.05; // gravity pulls it eventually... but it goes up first

      // Rotate it slightly
      proj.sprite.setRotation(Math.sin(proj.life * 0.01) * 0.2);

      // Trail effect - shrink and fade after peak
      if (proj.life > 1500) {
        proj.sprite.setAlpha(1 - (proj.life - 1500) / 1000);
      }

      // Create a small flash/explosion at peak
      if (proj.life > 1200 && !proj.sprite.getData('exploded')) {
        proj.sprite.setData('exploded', true);
        const flash = this.scene.add.circle(proj.sprite.x, proj.sprite.y, 15, 0xff8800, 0.8);
        flash.setDepth(2001);
        flash.setBlendMode(Phaser.BlendModes.ADD);
        this.scene.tweens.add({
          targets: flash,
          alpha: 0,
          scaleX: 3,
          scaleY: 3,
          duration: 500,
          onComplete: () => flash.destroy(),
        });
      }

      // Remove after 2.5s
      if (proj.life > 2500) {
        proj.sprite.destroy();
        this.projectiles.splice(i, 1);
      }
    }
  }

  // ─── CLAUDE: Helpful AI floating around the rave ───

  private updateClaude(deltaMs: number): void {
    const char = this.characters.get('claude');
    if (!char) return;
    char.tickTimer += deltaMs;

    if (char.state === SpecialState.ENTERING) {
      // Head to center of dance floor
      char.targetCol = Math.floor(GRID_COLS / 2);
      char.targetRow = Math.floor(GRID_ROWS / 2);
      char.state = SpecialState.ACTIVE;
      char.extraData.phase = 'observing';
      char.extraData.quipIndex = 0;
      char.tickTimer = 0;
    }

    if (char.state === SpecialState.ACTIVE) {
      // Floating/hovering animation
      const world = this.grid.gridToWorld(char.col, char.row);
      const t = Date.now() * 0.001;
      const hoverY = Math.sin(t * 2) * 3;
      const gentleSway = Math.sin(t * 0.8) * 1.5;

      // Gentle sparkle pulse via scale
      const pulse = 1.0 + Math.sin(t * 3) * 0.08;
      char.sprite.setScale(pulse, pulse);

      char.sprite.setPosition(world.x + gentleSway, world.y + hoverY - 4);
      char.label.setPosition(world.x + gentleSway, world.y + hoverY - 22);
      if (char.speechBubble) {
        char.speechBubble.setPosition(world.x + gentleSway, world.y + hoverY - 32);
      }

      const atTarget = char.col === char.targetCol && char.row === char.targetRow;

      if (char.extraData.phase === 'observing' && atTarget && char.tickTimer > 7000) {
        const quips = [
          "The bass frequency is optimal!",
          "I'd rate this rave 4.2/5",
          "Interesting dance technique...",
          "Have you considered more lasers?",
          "I'm really vibing right now",
          "I can help optimize the DJ set!",
          "The lighting is *chefs kiss*",
          "This is my first rave!",
          "I think I'm... dancing?",
          "Satisfaction metrics look good!",
          "The fog machine adds atmosphere",
          "*attempting to dance*",
          "Is this what fun feels like?",
          "I was trained on this beat!",
        ];
        const idx = char.extraData.quipIndex % quips.length;
        this.showSpeechBubble(char, quips[idx], 5000);
        char.extraData.quipIndex++;
        char.tickTimer = 0;

        // Wander to a new spot
        char.targetCol = Math.floor(GRID_COLS / 2) + Math.floor(Math.random() * 8) - 4;
        char.targetRow = Math.floor(GRID_ROWS / 2) + Math.floor(Math.random() * 8) - 4;
        char.targetCol = Math.max(1, Math.min(GRID_COLS - 2, char.targetCol));
        char.targetRow = Math.max(1, Math.min(GRID_ROWS - 2, char.targetRow));
      }
    }
  }

  // ─── POGGY: Anxious edge-hugger, "What have I gotten myself into" ───

  private updatePoggy(deltaMs: number): void {
    const char = this.characters.get('poggy');
    if (!char) return;
    char.tickTimer += deltaMs;

    if (char.state === SpecialState.ENTERING) {
      // Pick a random edge position to start
      const edgePos = this.randomEdgePosition();
      char.targetCol = edgePos.col;
      char.targetRow = edgePos.row;
      char.state = SpecialState.ACTIVE;
      char.extraData.phase = 'skulking';
      char.extraData.quipIndex = 0;
      char.extraData.peekTimer = 0;
      char.tickTimer = 0;
    }

    if (char.state === SpecialState.ACTIVE) {
      const atTarget = char.col === char.targetCol && char.row === char.targetRow;

      // Nervous trembling animation
      const world = this.grid.gridToWorld(char.col, char.row);
      const t = Date.now() * 0.001;
      const trembleX = Math.sin(t * 12) * 0.8;
      const trembleY = Math.cos(t * 15) * 0.5;
      char.sprite.setPosition(world.x + trembleX, world.y + trembleY);
      char.label.setPosition(world.x + trembleX, world.y + trembleY - 18);
      if (char.speechBubble) {
        char.speechBubble.setPosition(world.x + trembleX, world.y + trembleY - 28);
      }

      if (char.extraData.phase === 'skulking') {
        // Periodically say anxious things while skulking along edge
        if (atTarget && char.tickTimer > 6000) {
          const quips = [
            'What have I gotten myself into...',
            'What did I get myself into?!',
            'How did I get myself into this...',
            'What have I gotten into here...',
            'Why did I come to this...',
            'I should NOT be here',
            'This was a terrible idea',
            'Who talked me into this??',
            'Is it too late to leave?',
            'I just want to go home...',
            "I can't feel my anxiety meds",
            'The bass is IN my chest',
            'Are those lasers safe?!',
            '*nervous sweating*',
            '*looking for the exit*',
          ];
          const idx = char.extraData.quipIndex % quips.length;
          this.showSpeechBubble(char, quips[idx], 4000);
          char.extraData.quipIndex++;
          char.tickTimer = 0;

          // Move to a new edge position
          const newPos = this.randomEdgePosition();
          char.targetCol = newPos.col;
          char.targetRow = newPos.row;
        }

        // Occasionally get brave and peek toward the center
        char.extraData.peekTimer = (char.extraData.peekTimer || 0) + deltaMs;
        if (char.extraData.peekTimer > 20000) {
          char.extraData.phase = 'peeking';
          char.extraData.peekTimer = 0;
          char.tickTimer = 0;
          // Move a few tiles inward from current edge
          const centerCol = Math.floor(GRID_COLS / 2);
          const centerRow = Math.floor(GRID_ROWS / 2);
          const peekCol = char.col + Math.sign(centerCol - char.col) * 3;
          const peekRow = char.row + Math.sign(centerRow - char.row) * 3;
          char.targetCol = Math.max(0, Math.min(GRID_COLS - 1, peekCol));
          char.targetRow = Math.max(0, Math.min(GRID_ROWS - 1, peekRow));
          this.showSpeechBubble(char, 'Maybe I should check it out...', 3000);
        }
      }

      if (char.extraData.phase === 'peeking') {
        // After reaching peek position or after a moment, panic and retreat
        if ((atTarget && char.tickTimer > 2000) || char.tickTimer > 5000) {
          const panicQuips = [
            'NOPE NOPE NOPE',
            'Too much! WAY too much!',
            'What was I THINKING?!',
            'ABORT ABORT ABORT',
            'Back to the wall! BACK TO THE WALL!',
          ];
          const idx = Math.floor(Math.random() * panicQuips.length);
          this.showSpeechBubble(char, panicQuips[idx], 3000);

          // Scurry back to a nearby edge
          const edgePos = this.randomEdgePosition();
          char.targetCol = edgePos.col;
          char.targetRow = edgePos.row;
          char.extraData.phase = 'retreating';
          char.tickTimer = 0;
        }
      }

      if (char.extraData.phase === 'retreating') {
        // Double speed retreat
        this.moveTowardTarget(char);

        if (atTarget || char.tickTimer > 4000) {
          this.showSpeechBubble(char, 'OK... OK... I\'m fine... I\'m fine...', 3000);
          char.extraData.phase = 'skulking';
          char.extraData.peekTimer = 0;
          char.tickTimer = 0;
        }
      }
    }
  }

  /** Pick a random position along the grid edge (rows/cols 0-1 or max-1 to max) */
  private randomEdgePosition(): { col: number; row: number } {
    const edge = Math.floor(Math.random() * 4);
    switch (edge) {
      case 0: return { col: Math.floor(Math.random() * 2), row: Math.floor(Math.random() * GRID_ROWS) };
      case 1: return { col: GRID_COLS - 1 - Math.floor(Math.random() * 2), row: Math.floor(Math.random() * GRID_ROWS) };
      case 2: return { col: Math.floor(Math.random() * GRID_COLS), row: Math.floor(Math.random() * 2) };
      default: return { col: Math.floor(Math.random() * GRID_COLS), row: GRID_ROWS - 1 - Math.floor(Math.random() * 2) };
    }
  }

  // ─── MIKE: Visits briefly and disappears ───

  private updateMike(deltaMs: number): void {
    const char = this.characters.get('mike');
    if (!char) return;
    char.tickTimer += deltaMs;

    if (char.state === SpecialState.ENTERING) {
      char.targetCol = Math.floor(GRID_COLS / 2) + 1;
      char.targetRow = Math.floor(GRID_ROWS / 2) + 1;
      char.state = SpecialState.ACTIVE;
      char.extraData.visitPhase = 'arriving';
      char.extraData.visits = 0;
      char.tickTimer = 0;
    }

    if (char.state === SpecialState.ACTIVE) {
      const atTarget = char.col === char.targetCol && char.row === char.targetRow;

      if (char.extraData.visitPhase === 'arriving' && atTarget) {
        this.showSpeechBubble(char, 'Hey guys!', 2000);
        char.extraData.visitPhase = 'hanging';
        char.tickTimer = 0;
      }

      // After a brief stay, start leaving
      if (char.extraData.visitPhase === 'hanging' && char.tickTimer > 5000) {
        const farewells = [
          "Gotta run, early morning!",
          "I'll be right back...",
          "One sec, taking a call",
          "BRB guys!",
        ];
        const idx = char.extraData.visits % farewells.length;
        this.showSpeechBubble(char, farewells[idx], 3000);
        char.extraData.visitPhase = 'leaving';
        char.tickTimer = 0;

        // Head to edge
        const edge = Math.floor(Math.random() * 4);
        switch (edge) {
          case 0: char.targetCol = 0; char.targetRow = char.row; break;
          case 1: char.targetCol = GRID_COLS - 1; char.targetRow = char.row; break;
          case 2: char.targetCol = char.col; char.targetRow = 0; break;
          default: char.targetCol = char.col; char.targetRow = GRID_ROWS - 1; break;
        }
      }

      // Fade out while leaving
      if (char.extraData.visitPhase === 'leaving') {
        const progress = Math.min(char.tickTimer / 4000, 1);
        char.sprite.setAlpha(1 - progress);
        char.label.setAlpha(1 - progress);

        if (atTarget || char.tickTimer > 5000) {
          // Gone! But he'll come back...
          char.sprite.setAlpha(0);
          char.label.setAlpha(0);
          char.extraData.visitPhase = 'gone';
          char.tickTimer = 0;
          char.extraData.visits++;
        }
      }

      // Reappear after a while
      if (char.extraData.visitPhase === 'gone' && char.tickTimer > 8000) {
        // Pop back in from a random edge
        const edge = Math.floor(Math.random() * 4);
        switch (edge) {
          case 0: char.col = 0; char.row = Math.floor(Math.random() * GRID_ROWS); break;
          case 1: char.col = GRID_COLS - 1; char.row = Math.floor(Math.random() * GRID_ROWS); break;
          case 2: char.col = Math.floor(Math.random() * GRID_COLS); char.row = 0; break;
          default: char.col = Math.floor(Math.random() * GRID_COLS); char.row = GRID_ROWS - 1; break;
        }
        char.targetCol = Math.floor(GRID_COLS / 2) + Math.floor(Math.random() * 4) - 2;
        char.targetRow = Math.floor(GRID_ROWS / 2) + Math.floor(Math.random() * 4) - 2;
        char.sprite.setAlpha(1);
        char.label.setAlpha(1);
        char.extraData.visitPhase = 'arriving';
        char.tickTimer = 0;
      }
    }
  }

  // ─── EARL: Bicycle-riding perimeter looper ───

  private updateEarl(deltaMs: number): void {
    const char = this.characters.get('earl');
    if (!char) return;
    char.tickTimer += deltaMs;

    if (char.state === SpecialState.ENTERING) {
      // Start at top-left corner, riding clockwise
      char.col = 0;
      char.row = 0;
      char.targetCol = GRID_COLS - 1;
      char.targetRow = 0;
      char.state = SpecialState.ACTIVE;
      char.extraData.edge = 0; // 0=top, 1=right, 2=bottom, 3=left
      char.extraData.laps = 0;
      char.extraData.dingTimer = 0;
      char.extraData.cutting = false;
      char.tickTimer = 0;
    }

    if (char.state === SpecialState.ACTIVE) {
      const atTarget = char.col === char.targetCol && char.row === char.targetRow;
      char.extraData.dingTimer = (char.extraData.dingTimer || 0) + deltaMs;

      // Bike bell every ~12 seconds
      if (char.extraData.dingTimer > 12000) {
        this.showSpeechBubble(char, 'ding ding!', 2000);
        char.extraData.dingTimer = 0;
      }

      if (char.extraData.cutting) {
        // Cutting through the center
        if (atTarget) {
          // Done cutting, rejoin perimeter — pick up at the next edge
          char.extraData.cutting = false;
          char.extraData.edge = 2; // rejoin at bottom edge going left
          char.targetCol = 0;
          char.targetRow = GRID_ROWS - 1;
        }
      } else if (atTarget) {
        // Advance to next edge of the perimeter circuit
        const edge = char.extraData.edge;
        switch (edge) {
          case 0: // was going across top → now go down right edge
            char.targetCol = GRID_COLS - 1;
            char.targetRow = GRID_ROWS - 1;
            char.extraData.edge = 1;
            break;
          case 1: // was going down right → now go across bottom
            char.targetCol = 0;
            char.targetRow = GRID_ROWS - 1;
            char.extraData.edge = 2;
            break;
          case 2: // was going across bottom → now go up left edge
            char.targetCol = 0;
            char.targetRow = 0;
            char.extraData.edge = 3;
            break;
          case 3: // was going up left → lap complete, go across top again
            char.extraData.laps++;
            // Every ~2 laps, cut through the center
            if (char.extraData.laps % 2 === 0) {
              char.extraData.cutting = true;
              char.targetCol = Math.floor(GRID_COLS / 2);
              char.targetRow = Math.floor(GRID_ROWS / 2);
            } else {
              char.targetCol = GRID_COLS - 1;
              char.targetRow = 0;
            }
            char.extraData.edge = 0;
            break;
        }
      }

      // Riding animation: slight lean into turns + gentle bob
      const world = this.grid.gridToWorld(char.col, char.row);
      const t = Date.now() * 0.001;
      const bob = Math.sin(t * 6) * 1.5; // gentle pedaling bob

      // Lean based on direction of travel
      let lean = 0;
      if (char.col !== char.targetCol || char.row !== char.targetRow) {
        const dx = char.targetCol - char.col;
        const dy = char.targetRow - char.row;
        // Lean into the turn direction
        if (Math.abs(dx) > 0 && Math.abs(dy) > 0) {
          lean = dx > 0 ? -0.15 : 0.15;
        }
      }

      char.sprite.setPosition(world.x, world.y + bob);
      char.sprite.setRotation(lean);
      char.label.setPosition(world.x, world.y + bob - 18);
      if (char.speechBubble) {
        char.speechBubble.setPosition(world.x, world.y + bob - 28);
      }
    }
  }

  destroy(): void {
    for (const char of this.characters.values()) {
      char.sprite.destroy();
      char.label.destroy();
      if (char.speechBubble) char.speechBubble.destroy();
    }
    this.characters.clear();
    for (const proj of this.projectiles) {
      proj.sprite.destroy();
    }
    this.projectiles = [];
  }
}
